import { MaterialCommunityIcons } from "@expo/vector-icons";
import { ON_SITE_ISSUE_REASONS, type OnSiteIssueReason } from "@taskhealth/shared";
import { LinearGradient } from "expo-linear-gradient";
import { useEffect, useState } from "react";
import {
    View,
    Text,
    StyleSheet,
    Pressable,
    Modal,
    ActivityIndicator,
    TextInput,
    ScrollView
} from "react-native";

import { useKeyboardOffset } from "@/hooks/useKeyboardOffset";
import { colors, fonts, spacing, radius, shadows, gradients } from "@/lib/theme";

interface ReportIssueModalProps {
    visible: boolean;
    onConfirm: (
        reason: OnSiteIssueReason,
        note: string | undefined,
        location?: { lat: number; lng: number }
    ) => Promise<void> | void;
    onCancel: () => void;
    /** Opens straight on the note step with this reason picked (she can still
     *  go back and change it). The supervisory form uses it to send her here
     *  with "Aide not here" when she says the aide isn't there. */
    initialReason?: OnSiteIssueReason | null;
}

// The note step's prompt, per reason. Only the aide differs: for an absent
// aide we reach the agency, not the patient.
function noteCopy(reason: OnSiteIssueReason | null): { hint: string; placeholder: string } {
    if (reason === "aide_not_here") {
        return {
            hint: "Optional: anything that helps us reach the agency, like what the family said about the aide.",
            placeholder: "e.g. The family says the aide called out sick today"
        };
    }
    return {
        hint: "Optional: anything that helps us reach the patient or sort this out.",
        placeholder: "e.g. No answer at the door, lights are off"
    };
}

async function captureLocation(): Promise<{ lat: number; lng: number } | undefined> {
    try {
        const Location = await import("expo-location");
        const { status } = await Location.requestForegroundPermissionsAsync();
        if (status !== "granted") {
            return undefined;
        }
        const position = await Location.getCurrentPositionAsync({});
        return { lat: position.coords.latitude, lng: position.coords.longitude };
    } catch {
        return undefined;
    }
}

const REASONS = Object.entries(ON_SITE_ISSUE_REASONS) as Array<[OnSiteIssueReason, string]>;

export function ReportIssueModal({
    visible,
    onConfirm,
    onCancel,
    initialReason = null
}: ReportIssueModalProps) {
    // Two steps: pick a reason, then (optionally) add a note. Keeps the note from
    // hiding below the fold on the reason list.
    const [step, setStep] = useState<"reason" | "note">("reason");
    const [reason, setReason] = useState<OnSiteIssueReason | null>(null);
    const [note, setNote] = useState("");
    const [loading, setLoading] = useState(false);

    // Opened with a reason already chosen: land on its note step.
    useEffect(() => {
        if (visible && initialReason) {
            setReason(initialReason);
            setStep("note");
        }
    }, [visible, initialReason]);

    // Lift the sheet above the on-screen keyboard so the note input stays
    // visible. RN's Modal does not avoid the keyboard on its own, and on the
    // web PWA KeyboardAvoidingView is a no-op — useKeyboardOffset covers both.
    const keyboardOffset = useKeyboardOffset();

    const reset = () => {
        setStep("reason");
        setReason(null);
        setNote("");
        setLoading(false);
    };

    const handleSubmit = async () => {
        if (!reason) {
            return;
        }
        setLoading(true);
        const location = await captureLocation();
        await onConfirm(reason, note.trim() ? note.trim() : undefined, location);
        reset();
    };

    const handleCancel = () => {
        reset();
        onCancel();
    };

    const selectedLabel = reason ? ON_SITE_ISSUE_REASONS[reason] : "";
    const { hint: noteHint, placeholder: notePlaceholder } = noteCopy(reason);

    return (
        <Modal visible={visible} transparent animationType="slide" onRequestClose={handleCancel}>
            <View style={styles.backdrop}>
                <Pressable style={styles.backdropTouch} onPress={handleCancel} />
                <View
                    style={[styles.sheet, keyboardOffset > 0 && { marginBottom: keyboardOffset }]}
                >
                    <View style={styles.handle} />

                    {step === "reason" ? (
                        <>
                            <Text style={styles.title}>Can&apos;t start the visit?</Text>
                            <Text style={styles.subtitle}>
                                Pick what&apos;s happening. We&apos;ll reach the patient right away
                                and keep you posted in the chat. You&apos;ll be clocked in while we
                                sort it out, and once we&apos;ve reviewed what happened we&apos;ll
                                let you know about travel pay.
                            </Text>

                            <ScrollView style={styles.reasons} showsVerticalScrollIndicator={false}>
                                {REASONS.map(([key, label]) => {
                                    const selected = reason === key;
                                    return (
                                        <Pressable
                                            key={key}
                                            onPress={() => {
                                                // Pick + advance to the note step in one tap.
                                                setReason(key);
                                                setStep("note");
                                            }}
                                            style={[styles.chip, selected && styles.chipSelected]}
                                        >
                                            <Text
                                                style={[
                                                    styles.chipText,
                                                    selected && styles.chipTextSelected
                                                ]}
                                            >
                                                {label}
                                            </Text>
                                            {selected ? (
                                                <MaterialCommunityIcons
                                                    name="check-circle"
                                                    size={20}
                                                    color={colors.teal600}
                                                />
                                            ) : null}
                                        </Pressable>
                                    );
                                })}
                            </ScrollView>

                            <Pressable onPress={handleCancel} style={styles.secondaryButton}>
                                <Text style={styles.secondaryText}>Cancel</Text>
                            </Pressable>
                        </>
                    ) : (
                        <>
                            <Pressable
                                onPress={() => setStep("reason")}
                                style={styles.changeRow}
                                disabled={loading}
                            >
                                <MaterialCommunityIcons
                                    name="chevron-left"
                                    size={20}
                                    color={colors.teal600}
                                />
                                <Text style={styles.changeText}>{selectedLabel}</Text>
                            </Pressable>

                            <Text style={styles.title}>Add a note</Text>
                            <Text style={styles.subtitle}>{noteHint}</Text>

                            <TextInput
                                style={styles.note}
                                placeholder={notePlaceholder}
                                placeholderTextColor={colors.textTertiary}
                                value={note}
                                onChangeText={setNote}
                                multiline
                                maxLength={2000}
                                autoFocus
                            />

                            <Pressable
                                onPress={handleSubmit}
                                disabled={loading}
                                style={({ pressed }) => [pressed && styles.pressed]}
                            >
                                <LinearGradient
                                    colors={gradients.teal}
                                    start={{ x: 0, y: 0 }}
                                    end={{ x: 1, y: 1 }}
                                    style={[styles.confirmButton, shadows.teal]}
                                >
                                    {loading ? (
                                        <ActivityIndicator color="#FFFFFF" />
                                    ) : (
                                        <Text style={styles.confirmText}>SUBMIT</Text>
                                    )}
                                </LinearGradient>
                            </Pressable>

                            <Pressable
                                onPress={() => setStep("reason")}
                                style={styles.secondaryButton}
                                disabled={loading}
                            >
                                <Text style={styles.secondaryText}>Back</Text>
                            </Pressable>
                        </>
                    )}
                </View>
            </View>
        </Modal>
    );
}

const styles = StyleSheet.create({
    backdrop: {
        flex: 1,
        backgroundColor: "rgba(31, 21, 71, 0.5)",
        justifyContent: "flex-end"
    },
    backdropTouch: {
        flex: 1
    },
    sheet: {
        backgroundColor: colors.surface,
        borderTopLeftRadius: radius["2xl"],
        borderTopRightRadius: radius["2xl"],
        padding: spacing.xl,
        paddingBottom: spacing["3xl"],
        ...shadows.xl
    },
    handle: {
        width: 40,
        height: 4,
        backgroundColor: colors.gray300,
        borderRadius: 2,
        alignSelf: "center",
        marginBottom: spacing.xl
    },
    title: {
        fontFamily: fonts.bold,
        fontSize: 18,
        color: colors.textPrimary,
        marginBottom: spacing.xs
    },
    subtitle: {
        fontFamily: fonts.regular,
        fontSize: 13,
        color: colors.textSecondary,
        marginBottom: spacing.base
    },
    reasons: {
        maxHeight: 320
    },
    chip: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: radius.lg,
        paddingVertical: spacing.base,
        paddingHorizontal: spacing.base,
        marginBottom: spacing.sm
    },
    chipSelected: {
        borderColor: colors.teal500,
        backgroundColor: colors.teal50
    },
    chipText: {
        fontFamily: fonts.medium,
        fontSize: 14,
        color: colors.textPrimary
    },
    chipTextSelected: {
        fontFamily: fonts.semiBold,
        color: colors.teal600
    },
    changeRow: {
        flexDirection: "row",
        alignItems: "center",
        marginLeft: -spacing.xs,
        marginBottom: spacing.base
    },
    changeText: {
        fontFamily: fonts.semiBold,
        fontSize: 14,
        color: colors.teal600
    },
    note: {
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: radius.lg,
        padding: spacing.base,
        marginBottom: spacing.sm,
        minHeight: 96,
        fontFamily: fonts.regular,
        fontSize: 14,
        color: colors.textPrimary,
        textAlignVertical: "top"
    },
    confirmButton: {
        height: 56,
        borderRadius: radius.full,
        alignItems: "center",
        justifyContent: "center",
        marginTop: spacing.base
    },
    confirmDisabled: {
        opacity: 0.5
    },
    confirmText: {
        fontFamily: fonts.bold,
        fontSize: 14,
        color: "#FFFFFF",
        letterSpacing: 0.5
    },
    secondaryButton: {
        alignItems: "center",
        paddingTop: spacing.base
    },
    secondaryText: {
        fontFamily: fonts.medium,
        fontSize: 14,
        color: colors.textTertiary
    },
    pressed: {
        opacity: 0.9
    }
});
